import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../../components/controls/modal/Modal";
import Input from "../../components/controls/input/customInput/CustomInput";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import UserSelect from "../../components/userSelect/UserSelect";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import {
    getRunbookDetailRequest,
    createRunbookRequest,
    updateRunbookRequest,
} from "../../store/actions/runbooks.actions";
import {
    runbookLoading,
    runbookDetail,
    runbookDetailLoading,
} from "../../store/selectors/runbooks.selector";
import {
    RunbookStatus,
    getRunbookStatusLabel,
    formatDuration,
    UserSearchRefType,
} from "../../helpers/utils";
import { calcDurationMinutes, STATUS_OPTIONS, INITIAL_FORM } from "./utils";
import InputUnderline from "../../components/controls/input/InputUnderline";
import DropDown from "../../components/controls/dropdown/DropdownFluent";
import EntityFileUpload from "../../components/controls/fileUpload/EntityFileUpload";
import runbooksService from "../../store/services/runbooks.service";

const RunbookModal = ({ id, projectId, onSave, onClose }) => {
    const dispatch = useDispatch();
    const saving = useSelector(runbookLoading);
    const runbookItem = useSelector(runbookDetail);
    const runbookItemLoading = useSelector(runbookDetailLoading);
    const prevLoading = React.useRef(false);
    const isEdit = !!id;

    const [form, setForm] = useState(INITIAL_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [plannedDuration, setPlannedDuration] = useState(null);
    const [actualDuration, setActualDuration] = useState(null);

    useEffect(() => {
        setPlannedDuration(calcDurationMinutes(form.plannedStartDate, form.plannedEndDate));
    }, [form.plannedStartDate, form.plannedEndDate]);

    useEffect(() => {
        setActualDuration(calcDurationMinutes(form.actualStartDate, form.actualEndDate));
    }, [form.actualStartDate, form.actualEndDate]);

    useEffect(() => {
        setForm(INITIAL_FORM);
        setFormErrors({});
        if (isEdit && id) {
            dispatch(getRunbookDetailRequest({ id: id }));
        }
    }, [isEdit, id]);

    useEffect(() => {
        if (isEdit && runbookItem && runbookItem.id === id) {
            setForm({
                name: runbookItem.name || "",
                refId: runbookItem.refId || "",
                description: runbookItem.description,
                owner: runbookItem.owner || null,
                plannedStartDate: runbookItem.plannedStartDate,
                plannedEndDate: runbookItem.plannedEndDate,
                actualStartDate: runbookItem.actualStartDate,
                actualEndDate: runbookItem.actualEndDate,
                assignees: runbookItem.assignees || [],
                status: runbookItem.status ?? RunbookStatus.Draft,
                attachments: runbookItem.attachments || [],
            });
        }
    }, [runbookItem, isEdit, id]);

    // Close modal after save completes
    useEffect(() => {
        if (prevLoading.current && !saving) {
            onSave && onSave();
            onClose();
        }
        prevLoading.current = saving;
    }, [saving]);

    const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const validate = () => {
        const errs = {};
        if (!form.name?.trim()) errs.name = "Name is required";
        setFormErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;

        const showActual = isEdit && form.status >= RunbookStatus.InProgress;

        const payload = {
            name: form.name.trim(),
            refId: form.refId?.trim(),
            description: form.description || "",
            ownerId: form.owner?.id,
            plannedStartDate: form.plannedStartDate,
            plannedEndDate: form.plannedEndDate,
            actualStartDate: showActual ? form.actualStartDate : null,
            actualEndDate: showActual ? form.actualEndDate : null,
            assigneeIds: form.assignees ? form.assignees.map((a) => a.id) : [],
        };

        if (isEdit) {
            dispatch(
                updateRunbookRequest({
                    id: id,
                    projectId: projectId,
                    payload: { ...payload, status: form.status },
                    onSave,
                })
            );
        } else {
            dispatch(
                createRunbookRequest({
                    projectId: projectId,
                    payload: { ...payload, projectId }, onSave
                })
            );
        }
    };

    const showActual = isEdit && form.status >= RunbookStatus.InProgress;

    const modalBody = (
        <div className="d-flex flex-column gap-3">
            <div className="row gx-3 align-items-end">
                <div className={"col-9"}>
                    <InputUnderline
                        placeholder="Enter name"
                        value={form.name}
                        maxLength={255}
                        handleChange={(v) => update("name", v)}
                        error={formErrors.name}
                        isRequired
                        loading={runbookItemLoading}
                    />
                </div>
                <div className="col-3">
                    <UserSelect
                        refType={UserSearchRefType.Project}
                        refId={projectId}
                        value={form.owner}
                        label="Owner"
                        buttonLabel={"Select Owner"}
                        onChange={(val) => {
                            update("owner", val || null);
                        }}
                        loading={runbookItemLoading}
                    />
                </div>
            </div>

            <div className="row gx-3 align-items-end">
                <div className="col-4">
                    <Input
                        icon={false}
                        placeholder="Enter Change Ticket"
                        value={form.refId}
                        maxLength={255}
                        type="text"
                        handleChange={(v) => update("refId", v)}
                        label="Change Ticket"
                        loading={runbookItemLoading}
                    />
                </div>
                {
                    isEdit && (
                        <div className="col-3">
                            <DropDown
                                label="Status"
                                options={STATUS_OPTIONS}
                                defaultVal={getRunbookStatusLabel(form.status)}
                                onChange={(val) => update("status", val)}
                                loading={runbookItemLoading}
                            />
                        </div>
                    )
                }
            </div>
            <div className="row gx-3">
                <div className="col-12">
                    <UserSelect
                        multiple={true}
                        refType={UserSearchRefType.Project}
                        refId={projectId}
                        value={form.assignees}
                        label="Assignees"
                        buttonLabel={"Select Assignees"}
                        onChange={(selected) => {
                            update("assignees", selected || []);
                        }}
                        loading={runbookItemLoading}
                    />
                </div>
            </div>
            <div className="row gx-3 align-items-end">
                <div className="col">
                    <Input
                        label="Planned Start"
                        value={form.plannedStartDate}
                        type="datetime"
                        handleChange={(v) => { update("plannedStartDate", v); }}
                        maxDate={form.plannedEndDate}
                        loading={runbookItemLoading}
                    />

                </div>
                <div className="col">
                    <Input
                        label="Planned End"
                        value={form.plannedEndDate}
                        type="datetime"
                        handleChange={(v) => update("plannedEndDate", v)}
                        minDate={form.plannedStartDate}
                        loading={runbookItemLoading}
                    />

                </div>
                <div className="col-2">
                    <label className="Input_label">Duration</label>
                    <p
                        style={{ border: "1px solid #342C82" }}
                        className="flex font-figtree justify-center items-center text-xs text-[#1F1F1F] font-semibold bg-[#EDECF8] text-center py-[4px] h-[40px] rounded-md"
                    >
                        {plannedDuration !== null ? formatDuration(plannedDuration) : "-"}
                    </p>
                </div>
            </div>

            {showActual && (
                <div className="row gx-3 align-items-end">
                    <div className="col">
                        <Input
                            label="Actual Start"
                            value={form.actualStartDate}
                            type="datetime"
                            handleChange={(v) => update("actualStartDate", v)}
                            maxDate={form.actualEndDate}
                            loading={runbookItemLoading}
                        />

                    </div>
                    <div className="col">
                        <Input
                            label="Actual End"
                            value={form.actualEndDate}
                            type="datetime"
                            handleChange={(v) => update("actualEndDate", v)}
                            minDate={form.actualStartDate}
                            loading={runbookItemLoading}
                        />

                    </div>
                    <div className="col-2">
                        <label className="Input_label">Duration</label>
                        <p
                            style={{ border: "1px solid #342C82" }}
                            className="flex font-figtree justify-center items-center text-xs text-[#1F1F1F] font-semibold bg-[#EDECF8] text-center py-[4px] h-[40px] rounded-md"
                        >
                            {actualDuration ? formatDuration(actualDuration) : "-"}
                        </p>
                    </div>
                </div>
            )}
            <div className="row gx-3">
                <div className="col-12">
                    <RichTextEditor
                        label="Description"
                        content={form.description}
                        onChange={(v) => update("description", v)}
                        loading={runbookItemLoading}
                    />
                </div>
            </div>
            <div className="row gx-3">
                <div className="col-12">
                    <EntityFileUpload
                        entityId={id}
                        entityType="Runbook"
                        attachments={form.attachments}
                        onAttachmentsChange={() => {
                            if (id) {
                                runbooksService.getRunbookDetailRequest(id)
                                    .then((runbookData) => {
                                        update("attachments", runbookData.attachments);
                                    })
                                    .catch((error) => {
                                        console.error("Error fetching runbook data:", error);
                                    });
                            }
                        }}
                        disabled={runbookItemLoading || !id}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <Modal
            title={isEdit ? "Edit Runbook" : "Create Runbook"}
            body={modalBody}
            handleCancel={onClose}
            handleSave={handleSave}
            saveBtnLabel={isEdit ? "Update" : "Create"}
            cancelBtnLabel="Cancel"
            isLoading={saving}
            size="large"
        />
    );
};

export default RunbookModal;
